'use client';

import { ProfileDetailTabType } from '@diritto/common/account/constants/profile.constant';
import { ProfileReportRequestDto } from '@diritto/common/account/dtos/profile-report.dtos';
import { ActionType } from '@diritto/common/app/inapp-event.constant';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { Typography } from '@diritto/common/style/typography';
import { TrackerExtraInfo, TrackerPosition } from '@diritto/common/tracker/tracking-event.constant';
import { HttpStatus } from '@diritto/common/utils/requester/httpStatus';
import { ProfileReportApi } from 'api/ApiHelper';
import { FollowProfileButton } from 'app-components/accounts/FollowProfileButton';
import { Profile as ProfileComponent } from 'app-components/accounts/profiles/Profile';
import { ProfileContentList } from 'app-components/accounts/profiles/ProfileContentList';
import { ProfileJsonLd } from 'app-components/accounts/profiles/ProfileJsonLd';
import { ProfileTab } from 'app-components/accounts/profiles/ProfileTab';
import { FancyShareList } from 'app-components/fancyShare/FancyShareList';
import { ProfileReplyList } from 'app-components/reply/list/ProfileReplyList';
import { RollingList } from 'app-components/rolling/RollingList';
import { AxiosError } from 'axios';
import { ButtonSize, ButtonType } from 'constants/button';
import { ModalType } from 'constants/modal';
import { useAuthState } from 'contexts/AuthProvider';
import { EpisodeReplyProvider } from 'contexts/EpisodeReplyProvider';
import { FancyShareProvider } from 'contexts/FancyShareProvider';
import { useInAppState } from 'contexts/InAppProvider';
import { RollingProvider } from 'contexts/RollingProvider';
import { usePathname, useSearchParams } from 'next/navigation';
import { useMutateReportProfileQuery } from 'queries/account/useProfileReport';
import { useGetRelayProfileContentsInfiniteQuery } from 'queries/contents/useGetRelayProfileContentsInfiniteQuery';
import { useGetProfileFancySharesInfiniteQuery } from 'queries/fancyShare/userGetProfileFancySharesInfiniteQuery';
import { useGetProfileQuery } from 'queries/profile/useGetProfileQuery';
import { useBlockProfileMutation } from 'queries/profileBlock/useBlockProfileMutation';
import { useGetBlockProfileQuery } from 'queries/profileBlock/useGetBlockProfileQuery';
import { useUnBlockProfileMutation } from 'queries/profileBlock/useUnBlockProfileMutation';
import { useGetProfileRollingsInfiniteQuery } from 'queries/relay/useGetProfileRollingsInfiniteQuery';
import { useGetProfileReplyCountsQuery } from 'queries/reply/useGetProfileReplyCount';
import React, { useCallback, useEffect, useMemo } from 'react';
import { LinkButton } from 'ui-components/common/LinkButton';
import { ErrorStatusView } from 'ui-components/error/ErrorStatusView';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { Modal } from 'ui-components/modals/Modal';
import { ProfileReportModal } from 'ui-components/modals/ProfileReportModal';
import { ShareButton } from 'ui-components/navigations/HeaderButtons';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { useShare } from 'ui-hooks/useShare';
import { configure } from 'utils/configure';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';

interface ProfilePageProps {
  profileId: number;
  pageErrorCode?: HttpStatus;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.ProfileDetail,
};

export const ProfilePage = ({ profileId, pageErrorCode }: ProfilePageProps) => {
  useInAppHeader({ screen: WebViewScreen.Profile, hideHeader: true });
  useInAppStatusBar(WebViewScreen.Profile);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const thisUrl = `${configure.serviceUrl}${pathname}`;

  const { profile: myProfile, includeAdult, isLoggedIn } = useAuthState();
  const { data, error } = useGetProfileQuery(profileId);
  const requestSign = useRequestSign();
  const isMyProfile = useMemo(() => profileId === myProfile?.id, [myProfile?.id, profileId]);
  const { addActionEventListener } = useInAppState();

  const { data: profilesReplyCountData } = useGetProfileReplyCountsQuery(profileId);
  const { data: contentsData } = useGetRelayProfileContentsInfiniteQuery(profileId, { includeAdult });
  const profileFancySharesQuery = useGetProfileFancySharesInfiniteQuery(profileId, includeAdult);
  const profileRollingsQuery = useGetProfileRollingsInfiniteQuery(profileId, includeAdult);

  const { mutate: reportMutate } = useMutateReportProfileQuery(profileId, {
    onSuccess: () => showToast('신고가 접수되었어요.'),
    onError: () => showToast('일시적인 오류로 신고하지 못했어요. 잠시 뒤 다시 시도해주세요.'),
  });

  const handleReport = useCallback(async () => {
    try {
      const { isReported } = await ProfileReportApi.isReported(profileId);
      if (isReported) {
        showToast('이미 신고한 프로필이에요.');
        return;
      }
    } catch {
      showToast('일시적인 오류로 신고하지 못했어요. 잠시 뒤 다시 시도해주세요.');
      return;
    }

    ModalHandler.show(ModalType.ProfileReport, {
      onSubmit: (payload: ProfileReportRequestDto) => {
        reportMutate(payload);
      },
    });
  }, [profileId, reportMutate]);

  const { data: isBlockedData } = useGetBlockProfileQuery(profileId, isLoggedIn);
  const { mutate: blockMutate } = useBlockProfileMutation(profileId, {
    onSuccess: () => showToast('차단되었어요.'),
    onError: () => showToast('일시적인 오류로 차단하지 못했어요. 잠시 뒤 다시 시도해주세요.'),
  });
  const { mutate: unblockMutate } = useUnBlockProfileMutation(profileId, {
    onSuccess: () => showToast('차단 해제되었어요.'),
    onError: () => showToast('일시적인 오류로 차단 해제가 되지 않았어요. 잠시 뒤 다시 시도해주세요.'),
  });

  const extraMenu = useMemo(() => {
    if (isMyProfile) {
      return [];
    }

    if (isBlockedData?.block) {
      return [
        { title: '신고하기', onClick: isLoggedIn ? handleReport : requestSign },
        { title: '차단 해제하기', onClick: isLoggedIn ? () => unblockMutate() : requestSign },
      ];
    }
    return [
      { title: '신고하기', onClick: isLoggedIn ? handleReport : requestSign },
      { title: '차단하기', onClick: isLoggedIn ? () => blockMutate() : requestSign },
    ];
  }, [blockMutate, handleReport, isBlockedData?.block, isLoggedIn, isMyProfile, requestSign, unblockMutate]);

  const rightButtons = useMemo(
    () =>
      isMyProfile
        ? [
            <ShareButton
              key="share"
              params={{
                title: data?.profile?.nickname,
                text: data?.profile?.nickname,
              }}
            />,
          ]
        : [
            <FollowProfileButton key="follow" profileId={profileId} />,
            <ShareButton
              key="share"
              params={{
                title: data?.profile?.nickname,
                text: data?.profile?.nickname,
              }}
            />,
          ],
    [data?.profile?.nickname, isMyProfile, profileId],
  );

  const handleShare = useShare();
  useEffect(
    () =>
      addActionEventListener(params => {
        switch (params.type) {
          case ActionType.Share:
            handleShare({ title: data?.profile?.nickname, text: data?.profile?.nickname, url: thisUrl });
            break;
          default:
            break;
        }
      }),
    [addActionEventListener, data?.profile?.nickname, handleShare, thisUrl],
  );

  let tabType = searchParams?.get('tabType') as ProfileDetailTabType | undefined;
  if (!tabType) {
    tabType = !data?.profile?.isAuthor ? ProfileDetailTabType.FancyShare : ProfileDetailTabType.Content;
  }

  if (pageErrorCode || error) {
    return (
      <MutableHeaderLayout rightButtons={rightButtons} hiddenMenu={extraMenu} showHeaderInApp>
        <ErrorStatusView statusCode={pageErrorCode || (error as AxiosError)?.response?.status} />
      </MutableHeaderLayout>
    );
  }

  return (
    <>
      <ProfileJsonLd profile={data?.profile} />
      <MutableHeaderLayout rightButtons={rightButtons} hiddenMenu={extraMenu} showHeaderInApp>
        <ContentBodyContainer>
          <div className="flex flex-col items-center w-full">
            {data?.profile && data?.user && (
              <ProfileComponent className="mt-[24px]" profile={data.profile} user={data.user} />
            )}
            {isMyProfile && (
              <LinkButton
                className="w-full mt-[16px]"
                href={`/accounts/profiles/${data?.profile?.id}/edit`}
                label="프로필 수정"
                buttonType={ButtonType.SecondaryNeutral}
                size={ButtonSize.Small}
                buttonTypography={Typography.XXXSmall}
                fullWidth
              />
            )}
          </div>
          {!data?.user.isPartner && (
            <ProfileTab
              fancyShareCount={profileFancySharesQuery.data?.pages[0]?.totalCount}
              contentCount={contentsData?.pages[0]?.totalCount}
              replyCount={profilesReplyCountData?.count}
              rollingCount={profileRollingsQuery.data?.pages[0]?.totalCount}
              profile={data?.profile}
              tabType={tabType}
            />
          )}
        </ContentBodyContainer>
        {!data?.user.isPartner && (
          <>
            {tabType === ProfileDetailTabType.Content && <ProfileContentList profileId={profileId} />}
            {tabType === ProfileDetailTabType.Reply && (
              <EpisodeReplyProvider contentId={0} profileId={profileId}>
                <ProfileReplyList profileId={profileId} />
              </EpisodeReplyProvider>
            )}
            {tabType === ProfileDetailTabType.FancyShare && (
              <FancyShareProvider fancySharesQuery={profileFancySharesQuery}>
                <FancyShareList trackerExtraInfo={trackerExtraInfo} emptyDescription={'이멋공이 없어요'} />
              </FancyShareProvider>
            )}
            {tabType === ProfileDetailTabType.Rolling && (
              <RollingProvider rollingsQuery={profileRollingsQuery}>
                <RollingList trackerExtraInfo={trackerExtraInfo} emptyDescription={'롤링이 없어요'} />
              </RollingProvider>
            )}
          </>
        )}
      </MutableHeaderLayout>
      <Modal>
        <ProfileReportModal />
      </Modal>
    </>
  );
};
